import React, {Children, cloneElement, useRef, useEffect} from 'react';

const OutsideClickFocus: React.FC<{
  children: React.ReactElement;
  onClickOutside?: (event: MouseEvent | TouchEvent) => void;
  onFocusOutside?: (event: FocusEvent) => void;
}> = ({children, onClickOutside, onFocusOutside}) => {
  // inside click or touch is captured ( is active right now )
  const clickCaptured = useRef<boolean>(false);

  // inside focus is captured (is active right now)
  const focusCaptured = useRef<boolean>(false);

  useEffect(() => {
    document.addEventListener('mousedown', documentClick);
    document.addEventListener('touchstart', documentClick);
    document.addEventListener('focus', documentFocus, true);
    return () => {
      document.removeEventListener('mousedown', documentClick);
      document.removeEventListener('touchstart', documentClick);
      document.removeEventListener('focus', documentFocus, true);
    };
  }, []);

  function innerClick() {
    clickCaptured.current = true;
  }

  function innerFocus() {
    focusCaptured.current = true;
  }

  function documentClick(event: MouseEvent | TouchEvent) {
    // if the click is outside
    if (!clickCaptured.current && onClickOutside) {
      onClickOutside(event);
    }

    // reset click capture ( of inside the component )
    clickCaptured.current = false;
  }

  function documentFocus(event: FocusEvent) {
    if (!focusCaptured.current && onFocusOutside) {
      onFocusOutside(event);
    }

    // reset focus capture
    focusCaptured.current = false;
  }

  function getProps() {
    return {
      onMouseDown: innerClick,
      onTouchStart: innerClick,
      onFocus: innerFocus,
    };
  }

  if (!children) {
    throw new Error('OutsideClickFocus component needs children prop');
  }

  return (
    <>
      {Children.map(children, (child, i) => {
        return cloneElement(child, {
          ...getProps(),
        });
      })}
    </>
  );
};

export default OutsideClickFocus;
